// #44 Tek kullanıcı (oturum hesabı) seçici — PersonnelPickerDropdown deseninin birebir eşi:
// alana bağlı açılır DROPDOWN içinde arama + tablo + sayfalama (modal değil).
// Veri: /api/personnel/linkable-users — BAŞKA personele bağlı kullanıcılar listeye HİÇ gelmez
// (1 kullanıcı → 1 personel kuralı), düzenlenen personelin mevcut bağı ise listenin başındadır.
import { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { s, authHeaders } from './listPageStyles';

const PANEL_W = 620;
const PAGE_SIZE = 8;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function pageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  const start = Math.max(1, current - 1);
  const end = Math.min(total, current + 1);
  if (start > 1) { pages.push(1); if (start > 2) pages.push('…'); }
  for (let p = start; p <= end; p += 1) pages.push(p);
  if (end < total) { if (end < total - 1) pages.push('…'); pages.push(total); }
  return pages;
}

export function UserPickerDropdown({
  value, selectedLabel, onChange, disabled = false,
  personnelId, placeholder = 'Kullanıcı seç…', emptyHint, showToast,
}) {
  const [open, setOpen] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: PANEL_W, maxHeight: 460 });

  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ take: '200' });
      if (personnelId && personnelId !== 'new') params.set('personnelId', personnelId);
      const res = await fetch(`/api/personnel/linkable-users?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kullanıcı listesi alınamadı (${res.status})`);
      const items = unwrap(await res.json());
      setAllRows((Array.isArray(items) ? items : []).map((r) => ({
        id: pick(r, 'id', 'Id'),
        fullName: pick(r, 'fullName', 'FullName') ?? '',
        email: pick(r, 'email', 'Email') ?? '',
        isActive: pick(r, 'isActive', 'IsActive') ?? false,
      })));
    } catch (e) {
      showToast?.('error', e.message || 'Kullanıcı listesi alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [personnelId, showToast]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setPage(1);
    load();
  }, [open, load]);

  const filtered = useMemo(() => {
    const n = query.trim().toLocaleLowerCase('tr-TR');
    if (!n) return allRows;
    return allRows.filter((r) =>
      [r.fullName, r.email].some((v) => String(v ?? '').toLocaleLowerCase('tr-TR').includes(n)));
  }, [allRows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, (page - 1) * PAGE_SIZE + PAGE_SIZE), [filtered, page]);
  useEffect(() => { setPage(1); }, [query]);

  // Konumlandırma — tetikleyicinin altına; sığmazsa yukarı aç.
  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 12;
    const width = Math.min(PANEL_W, vw - margin * 2);
    const left = Math.min(Math.max(margin, r.left), vw - width - margin);
    const spaceBelow = vh - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const desired = 460;
    if (spaceBelow < 260 && spaceAbove > spaceBelow) {
      const maxHeight = Math.max(220, Math.min(desired, spaceAbove));
      setPos({ top: Math.max(margin, r.top - 4 - maxHeight), left, width, maxHeight });
    } else {
      const maxHeight = Math.max(220, Math.min(desired, spaceBelow));
      setPos({ top: r.bottom + 4, left, width, maxHeight });
    }
  }, [open]);

  // Dışarı tık / scroll / Esc → kapat.
  useEffect(() => {
    if (!open) return undefined;
    const inside = (el) => wrapRef.current?.contains(el) || panelRef.current?.contains(el);
    const onDown = (e) => { if (!inside(e.target)) setOpen(false); };
    const onScroll = (e) => { if (!inside(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pickRow = (row) => {
    onChange?.(row.id, { value: row.id, label: row.fullName || `Kullanıcı #${row.id}` });
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{ ...triggerStyle, ...(open ? triggerOpen : null), cursor: disabled ? 'default' : 'pointer' }}
      >
        <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: value ? 'var(--text-primary,#111827)' : 'var(--text-secondary,#9ca3af)' }}>
          {value ? (selectedLabel || `Kullanıcı #${value}`) : placeholder}
        </span>
        {!disabled && value && (
          <span role="button" title="Bağı kaldır" onClick={(e) => { e.stopPropagation(); onChange?.('', null); }} style={clearBtn}>×</span>
        )}
        <span aria-hidden="true" style={{ color: 'var(--text-secondary,#6b7280)', fontSize: 12 }}>▾</span>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ ...panelStyle, top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--border-color,#eef2f7)' }}>
            <input
              autoFocus
              style={{ ...s.input, flex: 1 }}
              placeholder="Ad, soyad veya e-posta ile ara…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <span style={{ fontSize: 12, color: 'var(--text-secondary,#6b7280)', whiteSpace: 'nowrap' }}>{filtered.length} kayıt</span>
          </div>

          <div style={{ flex: 1, overflow: 'auto', minHeight: 0, position: 'relative' }}>
            {loading && <div style={{ ...s.loadingOverlay, position: 'absolute' }}>Yükleniyor...</div>}
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Ad Soyad</th>
                  <th style={s.th}>E-posta</th>
                  <th style={s.th}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && !loading && (
                  <tr><td style={s.emptyCell} colSpan={3}>{emptyHint || 'Bağlanabilir kullanıcı bulunamadı'}</td></tr>
                )}
                {pageRows.map((r) => (
                  <tr key={r.id} style={s.tr} onClick={() => pickRow(r)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,115,234,0.07)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td style={s.td}><strong style={{ fontWeight: 600 }}>{r.fullName || `#${r.id}`}</strong></td>
                    <td style={s.tdMuted}>{r.email || '—'}</td>
                    <td style={s.tdMuted}>{r.isActive ? 'Aktif' : 'Pasif / kilitli'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 12px', borderTop: '1px solid var(--border-color,#eef2f7)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary,#6b7280)' }}>Satıra tıklayıp seçin</span>
              <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                <button style={page <= 1 ? s.pageBtnDisabled : s.pageBtn} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Önceki">‹</button>
                {pageList(page, totalPages).map((p, i) => (
                  p === '…'
                    ? <span key={`e${i}`} style={{ padding: '0 4px', color: 'var(--text-secondary,#9ca3af)' }}>…</span>
                    : <button key={p} style={p === page ? s.pageCurrent : s.pageBtn} onClick={() => setPage(p)}>{p}</button>
                ))}
                <button style={page >= totalPages ? s.pageBtnDisabled : s.pageBtn} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Sonraki">›</button>
              </div>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

const triggerStyle = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  padding: '9px 12px', borderRadius: 12, border: '1px solid var(--border-color,#d1d5db)',
  background: 'var(--input-bg,#fff)', fontSize: 14, boxSizing: 'border-box',
};
const triggerOpen = { borderColor: 'var(--accent)', boxShadow: '0 0 0 3px rgba(0,115,234,0.12)' };
const clearBtn = {
  cursor: 'pointer', color: 'var(--text-secondary,#6b7280)', fontSize: 16, lineHeight: 1,
  padding: '0 4px', borderRadius: 6,
};
const panelStyle = {
  position: 'fixed', zIndex: 100000, background: 'var(--bg-card,#fff)',
  border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 14,
  boxShadow: 'var(--shadow-pop, 0 18px 44px rgba(15,23,42,0.18))',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};

export default UserPickerDropdown;
